// src/pages/finance/RecordPayment.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeftIcon,
  BanknotesIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import apiClient from "../../api/client";
import { FireLoader } from "../../components/Loader";
import { showError, showSuccess } from "../../utils/alert";

const arrayFrom = (response) => {
  const payload = response?.data?.data ?? response?.data ?? [];
  if (Array.isArray(payload)) return payload;
  return payload.rows || payload.items || payload.students || payload.data || [];
};

const sle = (value) =>
  `SLE ${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))}`;

const studentName = (student) =>
  `${student?.first_name || ""} ${student?.middle_name || ""} ${student?.last_name || ""}`
    .replace(/\s+/g, " ")
    .trim();

const amountToCents = (value) => {
  const raw = String(value ?? "").trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(raw)) return null;

  const [wholePart, fractionPart = ""] = raw.split(".");
  const cents =
    Number(wholePart) * 100 +
    Number((fractionPart + "00").slice(0, 2));

  return Number.isSafeInteger(cents) ? cents : null;
};

const centsToAmount = (cents) => (Number(cents || 0) / 100).toFixed(2);

export default function RecordPayment() {
  const [loading, setLoading] = useState(true);
  const [feesLoading, setFeesLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [students, setStudents] = useState([]);
  const [years, setYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [policy, setPolicy] = useState(null);

  const [search, setSearch] = useState("");
  const [student, setStudent] = useState(null);
  const [yearId, setYearId] = useState("");
  const [termRows, setTermRows] = useState([]);
  const [selectedFeeIds, setSelectedFeeIds] = useState([]);
  const [receipt, setReceipt] = useState(null);

  const [form, setForm] = useState({
    amount_paid: "",
    bank_name: "",
    bank_reference: "",
    bank_payment_date: new Date().toISOString().slice(0, 10),
    notes: "",
  });

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];

    return students
      .filter((item) =>
        `${studentName(item)} ${item.admission_number || ""}`
          .toLowerCase()
          .includes(q),
      )
      .slice(0, 8);
  }, [students, search]);

  useEffect(() => {
    const run = async () => {
      try {
        const [studentsResponse, yearsResponse, termsResponse, policyResponse] =
          await Promise.all([
            apiClient.get("/students", {
              params: { page: 1, limit: 1000, status: "active" },
            }),
            apiClient.get("/academic-years"),
            apiClient.get("/academic-terms"),
            apiClient.get("/finance/policy"),
          ]);

        const yearList = arrayFrom(yearsResponse);
        setStudents(arrayFrom(studentsResponse));
        setYears(yearList);
        setTerms(arrayFrom(termsResponse));
        setPolicy(policyResponse?.data?.data || null);

        const currentYear =
          yearList.find((item) => item.is_current) || yearList[0];

        if (currentYear) {
          setYearId(String(currentYear.id));
        }
      } catch (error) {
        console.error(error);
        showError({ text: "Failed to load payment form information." });
      } finally {
        setLoading(false);
      }
    };

    run();
  }, []);

  useEffect(() => {
    const loadStudentFees = async () => {
      setTermRows([]);
      setSelectedFeeIds([]);
      setReceipt(null);

      if (policy?.is_free_education || !student?.id || !student.current_class_id || !yearId) return;

      try {
        setFeesLoading(true);

        const [feeResponse, ledgerResponse] = await Promise.all([
          apiClient.get("/finance/fees", {
            params: {
              class_id: student.current_class_id,
              academic_year_id: yearId,
            },
          }),
          apiClient.get(`/finance/students/${student.id}/ledger`, {
            params: { academic_year_id: yearId },
          }),
        ]);

        const configuredFees = arrayFrom(feeResponse);
        const ledger = ledgerResponse?.data?.data?.ledger || [];

        const rows = configuredFees
          .map((fee) => {
            const ledgerRow = ledger.find(
              (item) => Number(item.fee?.id) === Number(fee.id),
            );

            const term =
              fee.academicTerm ||
              terms.find(
                (item) => Number(item.id) === Number(fee.academic_term_id),
              ) ||
              null;

            const feeAmount = Number(fee.amount || 0);
            const totalPaid = Number(ledgerRow?.total_paid || 0);
            const balance = Number(
              ledgerRow?.balance ?? Math.max(0, feeAmount - totalPaid),
            );

            return {
              fee,
              term,
              total_paid: totalPaid,
              balance,
              status: ledgerRow?.status || (balance <= 0 ? "paid" : "unpaid"),
            };
          })
          .sort((a, b) => {
            const aOrder =
              Number(a.term?.term_number) ||
              Number(a.fee.academic_term_id) ||
              0;
            const bOrder =
              Number(b.term?.term_number) ||
              Number(b.fee.academic_term_id) ||
              0;
            return aOrder - bOrder;
          });

        setTermRows(rows);

        // Start with the current outstanding term selected. The admin can then
        // choose more terms or click "Select all outstanding".
        const currentOutstanding = rows.find(
          (row) =>
            row.balance > 0 &&
            (row.term?.is_current || row.term?.status === "active"),
        );
        const firstOutstanding = currentOutstanding || rows.find((row) => row.balance > 0);

        if (firstOutstanding) {
          setSelectedFeeIds([Number(firstOutstanding.fee.id)]);
        }
      } catch (error) {
        console.error(error);
        showError({ text: "Failed to load this student's fee balances." });
      } finally {
        setFeesLoading(false);
      }
    };

    loadStudentFees();
  }, [student, yearId, terms, policy?.is_free_education]);

  const selectedRows = useMemo(
    () =>
      termRows.filter((row) =>
        selectedFeeIds.includes(Number(row.fee.id)),
      ),
    [termRows, selectedFeeIds],
  );

  const selectedOutstandingCents = useMemo(
    () =>
      selectedRows.reduce(
        (sum, row) => sum + (amountToCents(row.balance) || 0),
        0,
      ),
    [selectedRows],
  );

  const enteredAmountCents = amountToCents(form.amount_paid);

  const allocationPreview = useMemo(() => {
    if (
      enteredAmountCents === null ||
      enteredAmountCents <= 0 ||
      !selectedRows.length
    ) {
      return [];
    }

    let remaining = enteredAmountCents;
    const allocations = [];

    for (const row of selectedRows) {
      if (remaining <= 0) break;

      const balanceCents = amountToCents(row.balance) || 0;
      const allocatedCents = Math.min(balanceCents, remaining);

      if (allocatedCents > 0) {
        allocations.push({
          row,
          amount_cents: allocatedCents,
        });
        remaining -= allocatedCents;
      }
    }

    return allocations;
  }, [enteredAmountCents, selectedRows]);

  const allocatedTotalCents = useMemo(
    () =>
      allocationPreview.reduce(
        (sum, item) => sum + item.amount_cents,
        0,
      ),
    [allocationPreview],
  );

  const chooseStudent = (item) => {
    setStudent(item);
    setSearch("");
    setReceipt(null);
  };

  const handleAmountChange = (event) => {
    const nextValue = event.target.value.replace(/,/g, "").trim();

    if (
      nextValue === "" ||
      /^\d+(?:\.\d{0,2})?$/.test(nextValue)
    ) {
      setForm((prev) => ({
        ...prev,
        amount_paid: nextValue,
      }));
    }
  };

  const toggleTerm = (feeId) => {
    const numericId = Number(feeId);
    setReceipt(null);

    setSelectedFeeIds((current) =>
      current.includes(numericId)
        ? current.filter((id) => id !== numericId)
        : [...current, numericId],
    );
  };

  const selectAllOutstanding = () => {
    const outstandingRows = termRows.filter((row) => row.balance > 0);
    const ids = outstandingRows.map((row) => Number(row.fee.id));
    const totalCents = outstandingRows.reduce(
      (sum, row) => sum + (amountToCents(row.balance) || 0),
      0,
    );

    setSelectedFeeIds(ids);
    setForm((prev) => ({
      ...prev,
      amount_paid: totalCents > 0 ? centsToAmount(totalCents) : "",
    }));
    setReceipt(null);
  };

  const clearTerms = () => {
    setSelectedFeeIds([]);
    setForm((prev) => ({ ...prev, amount_paid: "" }));
    setReceipt(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!student) {
      return showError({ text: "Select a student first." });
    }

    if (!selectedRows.length) {
      return showError({ text: "Select at least one outstanding term." });
    }

    if (enteredAmountCents === null || enteredAmountCents <= 0) {
      return showError({
        text: "Enter a valid amount with no more than 2 decimal places.",
      });
    }

    if (enteredAmountCents > selectedOutstandingCents) {
      return showError({
        text: `The entered amount is greater than the selected term balance of ${sle(
          centsToAmount(selectedOutstandingCents),
        )}.`,
      });
    }

    if (allocatedTotalCents !== enteredAmountCents) {
      return showError({
        text: "The full bank-slip amount could not be allocated to the selected terms.",
      });
    }

    if (!form.bank_reference.trim() || !form.bank_payment_date) {
      return showError({
        text: "Enter the bank receipt/reference number and payment date.",
      });
    }

    try {
      setSaving(true);

      const allocations = allocationPreview.map((item) => ({
        school_fee_id: item.row.fee.id,
        amount_paid: centsToAmount(item.amount_cents),
      }));

      const response = await apiClient.post("/finance/payments", {
        student_id: student.id,
        allocations,
        bank_name: form.bank_name.trim() || null,
        bank_reference: form.bank_reference.trim(),
        bank_payment_date: form.bank_payment_date,
        notes: form.notes.trim() || null,
      });

      const result = response?.data?.data || {};
      setReceipt({
        batch: result.batch || null,
        allocations: result.allocations || [],
      });

      // Update the visible balances immediately from the server's authoritative
      // allocation result.
      if (Array.isArray(result.allocations)) {
        setTermRows((current) =>
          current.map((row) => {
            const updated = result.allocations.find(
              (item) =>
                Number(item.school_fee_id) === Number(row.fee.id),
            );

            if (!updated) return row;

            return {
              ...row,
              total_paid: Number(updated.total_paid || 0),
              balance: Number(updated.balance || 0),
              status: updated.status || row.status,
            };
          }),
        );
      }

      setSelectedFeeIds([]);
      setForm((prev) => ({
        ...prev,
        amount_paid: "",
        bank_name: "",
        bank_reference: "",
        notes: "",
      }));

      await showSuccess({
        text:
          response?.data?.message ||
          "Bank payment recorded successfully.",
      });
    } catch (error) {
      console.error(error);
      await showError({
        text:
          error.response?.data?.message ||
          "Failed to record payment.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <FireLoader
        fullScreen={true}
        size="lg"
        text="LOADING..."
      />
    );
  }

  if (policy?.is_free_education) {
    return (
      <div className="mx-auto max-w-4xl space-y-5 p-4 sm:p-6 lg:p-8">
        <div className="flex items-center gap-3">
          <Link to="/app/finance" className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"><ArrowLeftIcon className="h-5 w-5" /></Link>
          <div><h1 className="text-2xl font-bold text-slate-900">Student Tuition</h1><p className="text-sm text-slate-500">Free Quality School Education policy</p></div>
        </div>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 sm:p-8">
          <ShieldCheckIcon className="h-12 w-12 text-emerald-600" />
          <h2 className="mt-4 text-xl font-bold text-emerald-900">No tuition payment is required</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-emerald-800">This school uses the Free Education tuition model. Student tuition is SLE 0.00, so bank-slip tuition payments cannot be recorded.</p>
          <Link to="/app/finance/fees" className="mt-5 inline-flex rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800">View School Funding Setup</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6 lg:p-8">
      <div className="flex items-center gap-3">
        <Link
          to="/app/finance"
          className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Record Bank Payment
          </h1>
          <p className="text-sm text-slate-500">
            One bank slip can cover one term or several terms for the student.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-xs leading-relaxed text-blue-800">
        <strong>Important:</strong> PawaSchool records a bank payment that has
        already been made. It does not process or receive the student's money.
      </div>

      <div className="grid gap-5 lg:grid-cols-5">
        <div className="space-y-4 lg:col-span-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-bold text-slate-900">
              1. Select Student
            </h2>

            <div className="relative mt-3">
              <MagnifyingGlassIcon className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search name or admission number"
                className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-primary-400"
              />

              {matches.length > 0 && (
                <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
                  {matches.map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      onClick={() => chooseStudent(item)}
                      className="block w-full border-b border-slate-50 px-3 py-2.5 text-left hover:bg-slate-50"
                    >
                      <span className="block text-sm font-medium text-slate-800">
                        {studentName(item)}
                      </span>
                      <span className="text-xs text-slate-400">
                        {item.admission_number} ·{" "}
                        {item.class?.name ||
                          `Class #${item.current_class_id}`}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {student && (
              <div className="mt-4 rounded-xl bg-slate-50 p-3">
                <p className="text-sm font-bold text-slate-800">
                  {studentName(student)}
                </p>
                <p className="text-xs text-slate-500">
                  Admission: {student.admission_number || "—"}
                </p>
                <p className="text-xs text-slate-500">
                  Class:{" "}
                  {student.class?.name ||
                    `Class #${student.current_class_id}`}
                </p>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-bold text-slate-900">
              2. Academic Year
            </h2>
            <select
              value={yearId}
              onChange={(event) => setYearId(event.target.value)}
              className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-primary-400"
            >
              <option value="">Academic year</option>
              {years.map((year) => (
                <option key={year.id} value={year.id}>
                  {year.name || year.year}
                </option>
              ))}
            </select>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-bold text-slate-900">
                  3. Select Term(s)
                </h2>
                <p className="mt-0.5 text-[11px] text-slate-400">
                  Select every term covered by this bank slip.
                </p>
              </div>

              {!!termRows.some((row) => row.balance > 0) && (
                <button
                  type="button"
                  onClick={selectAllOutstanding}
                  className="text-xs font-semibold text-primary-600 hover:text-primary-700"
                >
                  Select all
                </button>
              )}
            </div>

            {feesLoading ? (
              <div className="py-8 text-center text-xs text-slate-400">
                Loading term balances...
              </div>
            ) : !student ? (
              <div className="py-8 text-center text-xs text-slate-400">
                Select a student to see term fees.
              </div>
            ) : !termRows.length ? (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
                No class fees are configured for this academic year.{" "}
                <Link
                  to="/app/finance/fees"
                  className="font-bold underline"
                >
                  Set class fees
                </Link>
                .
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                {termRows.map((row) => {
                  const feeId = Number(row.fee.id);
                  const checked = selectedFeeIds.includes(feeId);
                  const paid = row.balance <= 0;

                  return (
                    <label
                      key={feeId}
                      className={`flex items-center gap-3 rounded-xl border p-3 transition ${
                        paid
                          ? "cursor-not-allowed border-slate-100 bg-slate-50 opacity-65"
                          : checked
                            ? "cursor-pointer border-primary-300 bg-primary-50"
                            : "cursor-pointer border-slate-200 hover:border-primary-200"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={paid}
                        onChange={() => toggleTerm(feeId)}
                        className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                      />

                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-800">
                          {row.term?.name ||
                            row.term?.short_name ||
                            `Term ${row.term?.term_number || row.fee.academic_term_id}`}
                        </p>
                        <p className="text-[11px] text-slate-400">
                          Fee {sle(row.fee.amount)} · Paid{" "}
                          {sle(row.total_paid)}
                        </p>
                      </div>

                      <div className="text-right">
                        <p
                          className={`text-xs font-bold ${
                            paid
                              ? "text-emerald-600"
                              : "text-amber-600"
                          }`}
                        >
                          {paid ? "Paid" : sle(row.balance)}
                        </p>
                        {!paid && (
                          <p className="text-[10px] text-slate-400">
                            balance
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            {!!selectedFeeIds.length && (
              <button
                type="button"
                onClick={clearTerms}
                className="mt-3 text-xs text-slate-500 hover:text-slate-700"
              >
                Clear selected terms
              </button>
            )}
          </div>
        </div>

        <div className="space-y-4 lg:col-span-3">
          <form
            onSubmit={handleSubmit}
            className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6"
          >
            <div className="flex items-center gap-2">
              <BanknotesIcon className="h-5 w-5 text-primary-600" />
              <h2 className="text-sm font-bold text-slate-900">
                4. Bank Slip Details
              </h2>
            </div>

            {!!selectedRows.length && (
              <div className="mt-4 grid grid-cols-2 gap-3 rounded-xl bg-slate-50 p-4">
                <div>
                  <p className="text-[11px] text-slate-400">
                    Selected Terms
                  </p>
                  <p className="text-sm font-bold text-slate-800">
                    {selectedRows.length}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-400">
                    Total Outstanding
                  </p>
                  <p className="text-sm font-bold text-amber-600">
                    {sle(centsToAmount(selectedOutstandingCents))}
                  </p>
                </div>
              </div>
            )}

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <label className="text-xs font-medium text-slate-600">
                Amount on Bank Slip (SLE)
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={form.amount_paid}
                  onChange={handleAmountChange}
                  placeholder="e.g. 4500.00"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-primary-400"
                />
              </label>

              <label className="text-xs font-medium text-slate-600">
                Bank Payment Date
                <input
                  type="date"
                  value={form.bank_payment_date}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      bank_payment_date: event.target.value,
                    })
                  }
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-primary-400"
                />
              </label>

              <label className="text-xs font-medium text-slate-600">
                Bank Name{" "}
                <span className="text-slate-400">(optional)</span>
                <input
                  value={form.bank_name}
                  onChange={(event) =>
                    setForm({ ...form, bank_name: event.target.value })
                  }
                  placeholder="Bank name"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-primary-400"
                />
              </label>

              <label className="text-xs font-medium text-slate-600">
                Bank Receipt / Reference Number
                <input
                  value={form.bank_reference}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      bank_reference: event.target.value,
                    })
                  }
                  placeholder="e.g. DEP-839201"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm uppercase outline-none focus:border-primary-400"
                />
              </label>

              <label className="text-xs font-medium text-slate-600 sm:col-span-2">
                Notes <span className="text-slate-400">(optional)</span>
                <textarea
                  rows="3"
                  value={form.notes}
                  onChange={(event) =>
                    setForm({ ...form, notes: event.target.value })
                  }
                  className="mt-1 w-full resize-none rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-primary-400"
                />
              </label>
            </div>

            {!!allocationPreview.length && (
              <div className="mt-5 rounded-xl border border-slate-200">
                <div className="border-b border-slate-100 px-4 py-3">
                  <h3 className="text-xs font-bold text-slate-700">
                    Allocation Preview
                  </h3>
                  <p className="text-[11px] text-slate-400">
                    The bank-slip amount will be applied in term order.
                  </p>
                </div>

                <div className="divide-y divide-slate-100">
                  {allocationPreview.map((item) => (
                    <div
                      key={item.row.fee.id}
                      className="flex items-center justify-between gap-3 px-4 py-3"
                    >
                      <span className="text-xs font-medium text-slate-600">
                        {item.row.term?.name ||
                          item.row.term?.short_name ||
                          `Term ${item.row.term?.term_number || item.row.fee.academic_term_id}`}
                      </span>
                      <span className="text-xs font-bold text-primary-700">
                        {sle(centsToAmount(item.amount_cents))}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between bg-slate-50 px-4 py-3">
                  <span className="text-xs font-bold text-slate-700">
                    Bank Slip Total
                  </span>
                  <span className="text-sm font-bold text-slate-900">
                    {sle(centsToAmount(allocatedTotalCents))}
                  </span>
                </div>
              </div>
            )}

            <button
              disabled={!selectedRows.length || saving}
              className="mt-5 w-full rounded-lg bg-primary-600 px-5 py-3 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving
                ? "Recording..."
                : selectedRows.length > 1
                  ? `Record Payment for ${selectedRows.length} Terms`
                  : "Record Verified Bank Payment"}
            </button>
          </form>

          {receipt?.batch && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircleIcon className="h-5 w-5" />
                <h3 className="text-sm font-bold">
                  Bank Payment Recorded
                </h3>
              </div>

              <div className="mt-3 grid gap-2 text-xs text-emerald-900 sm:grid-cols-2">
                <p>
                  PawaSchool Receipt:{" "}
                  <strong>{receipt.batch.receipt_number}</strong>
                </p>
                <p>
                  Bank Reference:{" "}
                  <strong>{receipt.batch.bank_reference}</strong>
                </p>
                <p>
                  Total Amount:{" "}
                  <strong>{sle(receipt.batch.total_amount)}</strong>
                </p>
                <p>
                  Terms Covered:{" "}
                  <strong>{receipt.allocations.length}</strong>
                </p>
              </div>

              {!!receipt.allocations.length && (
                <div className="mt-4 divide-y divide-emerald-200 rounded-xl border border-emerald-200 bg-white/60">
                  {receipt.allocations.map((item) => {
                    const row = termRows.find(
                      (termRow) =>
                        Number(termRow.fee.id) ===
                        Number(item.school_fee_id),
                    );

                    return (
                      <div
                        key={item.school_fee_id}
                        className="flex items-center justify-between gap-3 px-3 py-2"
                      >
                        <span className="text-xs text-emerald-900">
                          {row?.term?.name ||
                            row?.term?.short_name ||
                            `Term #${item.academic_term_id}`}
                        </span>
                        <span className="text-xs font-bold text-emerald-900">
                          {sle(item.allocated_amount)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}